import React, { useEffect, useState } from 'react';
import { useForm, type FieldErrors } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LocationSelector from './LocationSelector';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import AlertMessage from '../common/AlertMessage';
import { GENRE_OPTIONS, toggleGenreSelection } from '../../shared/genres';
import { AVAILABILITY_OPTIONS, toggleAvailabilitySelection } from '../../shared/availability';
import { INSTRUMENT_OPTIONS } from '../../shared/avatars';

const currentYear = new Date().getFullYear();

const registerSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  name: z.string().min(2, 'Name must be at least 2 characters'),
  birthYear: z
    .number({
      required_error: 'Please enter your birth year',
      invalid_type_error: 'Please enter a valid year',
    })
    .int('Please enter a valid year')
    .min(1900, 'Please enter a valid year')
    .max(currentYear, `Birth year must be ${currentYear} or earlier`),
  instrument: z.string().min(1, 'Please select an instrument'),
  skillLevel: z.string().min(1, 'Please select your skill level'),
  genres: z
    .array(z.string().min(1))
    .max(3, 'Select up to 3 genres'),
  availability: z
    .array(z.string().min(1))
    .max(3, 'Select up to 3 availability options'),
  location: z.object({
    lat: z.number(),
    lng: z.number(),
  }, {
    required_error: 'Please select a location',
    invalid_type_error: 'Please select a location',
  }),
  address: z.string().min(1, 'Please enter your location'),
  role: z.literal('user'),
});

type RegisterFormData = z.infer<typeof registerSchema>;

const STEP_ONE_FIELDS: (keyof RegisterFormData)[] = ['email', 'password', 'name', 'birthYear'];
const STEP_TWO_FIELDS: (keyof RegisterFormData)[] = ['instrument', 'skillLevel', 'genres', 'availability'];

const RegisterForm: React.FC = () => {
  const [step, setStep] = useState(1);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  
  const { register, handleSubmit, setValue, watch, trigger, formState: { errors } } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      role: 'user',
      genres: [],
      availability: [],
    }
  });

  const skillLevels = ['Beginner', 'Intermediate', 'Advanced', 'Professional'];
  const maxGenres = 3;

  useEffect(() => {
    register('genres');
    register('availability');
  }, [register]);

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setRegisterError(null);
      await registerUser({
        ...data,
      });
      navigate('/login', {
        replace: true,
        state: {
          registrationMessage: 'Registration completed. Please check your email to activate your account.',
        },
      });
    } catch (error) {
      console.error('Registration failed', error);
      setRegisterError(error instanceof Error ? error.message : 'Registration failed. Please try again.');
    }
  };

  const getStepByField = (field?: keyof RegisterFormData): number => {
    if (!field) return step;
    if (STEP_ONE_FIELDS.includes(field)) return 1;
    if (STEP_TWO_FIELDS.includes(field)) return 2;
    return 3;
  };

  const onInvalid = (formErrors: FieldErrors<RegisterFormData>) => {
    const firstInvalidField = Object.keys(formErrors)[0] as keyof RegisterFormData | undefined;
    const invalidStep = getStepByField(firstInvalidField);
    if (invalidStep !== step) {
      setStep(invalidStep);
    }
    setRegisterError(
      invalidStep < step
        ? 'Some required fields in previous steps are missing or invalid. Please review them.'
        : 'Please complete the required fields before continuing.'
    );
  };

  const handleLocationSelect = (location: { lat: number; lng: number }, address: string) => {
    setValue('location', location);
    setValue('address', address);
  };

  const genres = watch('genres') ?? [];
  const availability = watch('availability') ?? [];
  const isGenreLimitReached = genres.length >= maxGenres;
  const isAvailabilityLimitReached = availability.length >= maxGenres;
  const handleGenreToggle = (genre: string) => {
    const nextGenres = toggleGenreSelection(genres, genre, maxGenres);
    setValue('genres', nextGenres, { shouldValidate: true, shouldDirty: true });
  };
  const handleAvailabilityToggle = (option: string) => {
    const nextAvailability = toggleAvailabilitySelection(availability, option, maxGenres);
    setValue('availability', nextAvailability, { shouldValidate: true, shouldDirty: true });
  };
  const getGenreButtonClass = (isSelected: boolean, isDisabled: boolean) => {
    const base = 'px-3 py-2 text-sm rounded-md border transition-colors duration-300';
    if (isSelected) return `${base} bg-purple-600 border-purple-500 text-white`;
    if (isDisabled) return `${base} bg-gray-700 border-gray-600 text-gray-500 cursor-not-allowed`;
    return `${base} bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600`;
  };

  const nextStep = async () => {
    const fieldsToValidate = step === 1 ? STEP_ONE_FIELDS : STEP_TWO_FIELDS;
    const isValid = await trigger(fieldsToValidate);
    if (!isValid) {
      setRegisterError('Please complete the required fields before continuing.');
      return;
    }
    setRegisterError(null);
    setStep((currentStep) => currentStep + 1);
  };
  const prevStep = () => setStep(step - 1);

  return (
    <div className="bg-gray-800 rounded-lg shadow-xl p-6 md:p-8 w-full max-w-md mx-auto">
      <h2 className="text-2xl font-bold text-white mb-6">Create Your Musician Profile</h2>
      
      {registerError && (
        <div className="mb-4">
          <AlertMessage variant="error">{registerError}</AlertMessage>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label htmlFor="email\" className="block text-sm font-medium text-gray-300 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                {...register('email')}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                placeholder="your.email@example.com"
              />
              {errors.email && <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>}
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                {...register('password')}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                placeholder="********"
              />
              {errors.password && <p className="mt-1 text-sm text-red-400">{errors.password.message}</p>}
            </div>
            
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
                Name
              </label>
              <input
                id="name"
                type="text"
                {...register('name')}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                placeholder="Your name"
              />
              {errors.name && <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>}
            </div>

            <div>
              <label htmlFor="birthYear" className="block text-sm font-medium text-gray-300 mb-1">
                Birth Year
              </label>
              <input
                id="birthYear"
                type="number"
                min="1900"
                max={currentYear}
                {...register('birthYear', { valueAsNumber: true })}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                placeholder="1995"
              />
              {errors.birthYear && <p className="mt-1 text-sm text-red-400">{errors.birthYear.message}</p>}
            </div>
            
            <button
              type="button"
              onClick={nextStep}
              className="w-full flex justify-center items-center bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-300"
            >
              Next <ChevronRight className="ml-2 w-5 h-5" />
            </button>
          </div>
        )}
        
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <label htmlFor="instrument" className="block text-sm font-medium text-gray-300 mb-1">
                Instrument
              </label>
              <select
                id="instrument"
                {...register('instrument')}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
              >
                <option value="">Select your instrument</option>
                {INSTRUMENT_OPTIONS.map((instrument) => (
                  <option key={instrument} value={instrument}>
                    {instrument}
                  </option>
                ))}
              </select>
              {errors.instrument && <p className="mt-1 text-sm text-red-400">{errors.instrument.message}</p>}
            </div>
            
            <div>
              <label htmlFor="skillLevel" className="block text-sm font-medium text-gray-300 mb-1">
                Skill Level
              </label>
              <select
                id="skillLevel"
                {...register('skillLevel')}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
              >
                <option value="">Select your skill level</option>
                {skillLevels.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
              {errors.skillLevel && <p className="mt-1 text-sm text-red-400">{errors.skillLevel.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Preferred Genres</label>
              <p className="text-xs text-gray-400">Select up to {maxGenres} genres.</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {GENRE_OPTIONS.map((genre) => {
                  const isSelected = genres.includes(genre);
                  const isDisabled = !isSelected && isGenreLimitReached;
                  return (
                    <button
                      key={genre}
                      type="button"
                      onClick={() => handleGenreToggle(genre)}
                      disabled={isDisabled}
                      className={getGenreButtonClass(isSelected, isDisabled)}
                      aria-pressed={isSelected}
                    >
                      {genre}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-gray-400">{genres.length} / {maxGenres} selected</p>
              {errors.genres && <p className="mt-2 text-sm text-red-400">{errors.genres.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Availability</label>
              <p className="text-xs text-gray-400">Select up to {maxGenres} options.</p>
              <div className="mt-3 grid grid-cols-1 gap-2">
                {AVAILABILITY_OPTIONS.map((option) => {
                  const isSelected = availability.includes(option);
                  const isDisabled = !isSelected && isAvailabilityLimitReached;
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => handleAvailabilityToggle(option)}
                      disabled={isDisabled}
                      className={getGenreButtonClass(isSelected, isDisabled)}
                      aria-pressed={isSelected}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-gray-400">{availability.length} / {maxGenres} selected</p>
              {errors.availability && <p className="mt-2 text-sm text-red-400">{errors.availability.message}</p>}
            </div>
            
            <div className="flex justify-between space-x-4">
              <button
                type="button"
                onClick={prevStep}
                className="flex-1 flex justify-center items-center bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-md transition-colors duration-300"
              >
                <ChevronLeft className="mr-2 w-5 h-5" /> Back
              </button>
              
              <button
                type="button"
                onClick={nextStep}
                className="flex-1 flex justify-center items-center bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-300"
              >
                Next <ChevronRight className="ml-2 w-5 h-5" />
              </button>
            </div>
          </div>
        )}
        
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Your Location
              </label>
              <LocationSelector onLocationSelect={handleLocationSelect} />
              {errors.location && <p className="mt-1 text-sm text-red-400">Please select a location</p>}
              {errors.address && <p className="mt-1 text-sm text-red-400">{errors.address.message}</p>}
            </div>
            
            <div className="flex justify-between space-x-4">
              <button
                type="button"
                onClick={prevStep}
                className="flex-1 flex justify-center items-center bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-md transition-colors duration-300"
              >
                <ChevronLeft className="mr-2 w-5 h-5" /> Back
              </button>
              
              <button
                type="submit"
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-300"
              >
                Register
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default RegisterForm;
